import React from 'react';
import { Col, DatePicker, Radio, Row, Select, Typography } from 'antd';
import { HeaderRender } from 'antd/es/calendar/generateCalendar';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { calendarStore } from 'entities/calendar';
import { ReactComponent as ArrowBottom } from 'shared/assets/icons/dynamicColor/arrow-bottom.svg';

import { getMonthOptions } from '../../lib/getMonthOptions';

import styles from './CalendarHeader.module.scss';

type CalendarHeaderProps = Parameters<HeaderRender<Dayjs>>[0] & {
  title: string;
};

const CalendarHeader = ({ value, type, onChange, onTypeChange, title }: CalendarHeaderProps): React.ReactElement => {
  const { t } = useTranslation('Calendar');
  const selectMonth = useStore(calendarStore, (state) => state.selectMonth);
  const setViewType = useStore(calendarStore, (state) => state.setViewType);

  const months = value.localeData().months();
  const month = value.month();

  const handleMonthChange = (newMonth: number): void => {
    selectMonth(newMonth);
    onChange(value.clone().month(newMonth));
  };

  const handleTypeChange = (newType: 'month' | 'year'): void => {
    setViewType(newType);
    onTypeChange(newType);
  };

  return (
    <div className={styles.wrapper}>
      <Typography.Title level={4} className={styles.title}>
        {title}
      </Typography.Title>
      <Row gutter={8}>
        {type === 'month' && (
          <Col>
            <Select
              size="middle"
              virtual={false}
              value={month}
              className={styles.monthSelect}
              onChange={handleMonthChange}
            >
              {getMonthOptions(months)}
            </Select>
          </Col>
        )}
        <Col>
          <Radio.Group
            size="middle"
            className={styles.radioBtns}
            onChange={(e) => handleTypeChange(e.target.value)}
            value={type}
            optionType="button"
            options={[
              { value: 'month', label: t('calendarLayout.header.mode.month') },
              { value: 'year', label: t('calendarLayout.header.mode.year') },
            ]}
          />
        </Col>
        <Col>
          <DatePicker
            value={value}
            onChange={onChange}
            picker="year"
            suffixIcon={<ArrowBottom height="8px" width="12px" />}
            allowClear={false}
            className={styles.datePicker}
          />
        </Col>
      </Row>
    </div>
  );
};

const СalendarHeaderRender =
  (label: string): HeaderRender<Dayjs> =>
  ({ value, type, onChange, onTypeChange }) =>
    <CalendarHeader value={value} type={type} onChange={onChange} onTypeChange={onTypeChange} title={label} />;

export { СalendarHeaderRender };
